/**
 * ToolExecutionCard Component
 * Displays tool execution details in a collapsible card
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Wrench, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { ToolExecution } from '../types';

interface ToolExecutionCardProps {
  execution: ToolExecution;
}

export const ToolExecutionCard: React.FC<ToolExecutionCardProps> = ({ execution }) => {
  // Start collapsed by default
  const [isExpanded, setIsExpanded] = useState(false);

  // Get status icon and color
  const getStatusDisplay = () => {
    switch (execution.status) {
      case 'pending':
      case 'executing':
        return {
          icon: <Loader2 className="h-4 w-4 animate-spin" />,
          color: 'text-blue-600 dark:text-blue-400',
          bgColor: 'bg-blue-50 dark:bg-blue-900/20',
          borderColor: 'border-blue-200 dark:border-blue-800',
          label: 'Executing',
        };
      case 'success':
        return {
          icon: <CheckCircle className="h-4 w-4" />,
          color: 'text-green-600 dark:text-green-400',
          bgColor: 'bg-green-50 dark:bg-green-900/20',
          borderColor: 'border-green-200 dark:border-green-800',
          label: 'Success',
        };
      case 'error':
        return {
          icon: <XCircle className="h-4 w-4" />,
          color: 'text-red-600 dark:text-red-400',
          bgColor: 'bg-red-50 dark:bg-red-900/20',
          borderColor: 'border-red-200 dark:border-red-800',
          label: 'Error',
        };
    }
  };

  const statusDisplay = getStatusDisplay();

  // Format JSON for display
  const formatJson = (obj: any): string => {
    try {
      // If it's already a string, try to parse it first
      if (typeof obj === 'string') {
        try {
          const parsed = JSON.parse(obj);
          return JSON.stringify(parsed, null, 2);
        } catch {
          // If parsing fails (incomplete JSON during streaming), return as-is
          // Try to pretty-print anyway by adding line breaks after commas
          return obj
            .replace(/,\s*/g, ',\n  ')
            .replace(/{\s*/g, '{\n  ')
            .replace(/}\s*/g, '\n}')
            .replace(/\[\s*/g, '[\n  ')
            .replace(/]\s*/g, '\n]');
        }
      }
      // If it's already an object, stringify it
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  };

  return (
    <div
      className={`rounded-lg border ${statusDisplay.borderColor} ${statusDisplay.bgColor} overflow-hidden transition-all`}
    >
      {/* Header (always visible) */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-3 py-2 flex items-center gap-2 hover:opacity-80 transition-opacity"
      >
        {/* Expand/Collapse Icon */}
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-gray-600 dark:text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-600 dark:text-gray-400 flex-shrink-0" />
        )}

        {/* Tool Icon */}
        <Wrench className={`h-4 w-4 flex-shrink-0 ${statusDisplay.color}`} />

        {/* Tool Name */}
        <span className="text-sm font-medium text-gray-900 dark:text-white flex-shrink-0">
          {execution.tool_name}
        </span>

        {/* Status Icon */}
        <div className={`flex items-center gap-1 ml-auto ${statusDisplay.color}`}>
          {statusDisplay.icon}
          <span className="text-xs font-medium">{statusDisplay.label}</span>
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-3 pb-3 space-y-2 border-t border-gray-200 dark:border-gray-700">
          {/* Parameters */}
          {Object.keys(execution.tool_params).length > 0 && (
            <div>
              <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 mt-2 flex items-center gap-2">
                Parameters:
                {(execution.status === 'pending' || execution.status === 'executing') && (
                  <span className="text-xs text-blue-600 dark:text-blue-400 animate-pulse">
                    streaming...
                  </span>
                )}
              </div>
              <pre className="text-xs bg-white dark:bg-gray-800 rounded p-2 overflow-x-auto border border-gray-200 dark:border-gray-700 max-h-96 overflow-y-auto">
                <code className="text-gray-900 dark:text-gray-100 whitespace-pre-wrap break-words">
                  {formatJson(execution.tool_params)}
                </code>
              </pre>
            </div>
          )}

          {/* Result (if available) */}
          {execution.result !== undefined && (
            <div>
              <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Result:
              </div>
              <pre className="text-xs bg-white dark:bg-gray-800 rounded p-2 overflow-x-auto border border-gray-200 dark:border-gray-700">
                <code className="text-gray-900 dark:text-gray-100">
                  {formatJson(execution.result)}
                </code>
              </pre>
            </div>
          )}

          {/* Error (if any) */}
          {execution.error && (
            <div>
              <div className="text-xs font-medium text-red-700 dark:text-red-300 mb-1">
                Error:
              </div>
              <div className="text-xs bg-red-100 dark:bg-red-900/20 rounded p-2 text-red-900 dark:text-red-100">
                {execution.error}
              </div>
            </div>
          )}

          {/* Timestamp */}
          <div className="text-xs text-gray-500 dark:text-gray-400 pt-1">
            {execution.timestamp.toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })}
          </div>
        </div>
      )}
    </div>
  );
};
